import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Col,
  Row,
  NMForm,
  Button,
  usePub,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import { mitigatorActions, getCurrentReference } from "../../../store/";

const getReferenceFields = (isViewMode, isEditMode) => {
  const fields = [];
  if (isViewMode || isEditMode) {
    fields.push({
      label: "Reference ID",
      labelAlign: "top",
      attribute: "id",
      placeholder: "",
      readOnly: true,
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-131-px" },
    });
  }
  fields.push(
    {
      label: "Reference Name",
      labelAlign: "top",
      attribute: "name",
      placeholder: "Reference Name",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-131-px" },
    },
    {
      label: "Reference Link",
      labelAlign: "top",
      attribute: "link",
      placeholder: "Reference Link",
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-131-px" },
    },
  );
  return fields;
};
export const CreateNewReference = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = React.useState(false);
  const _ref = React.useRef(null);
  const viewRef = React.useRef(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get("tab");
  const referenceId = params.get("id");
  const currentReference = useSelector(getCurrentReference);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (currentReference && viewRef.current) {
      viewRef.current.updateFormData({
        id: currentReference.reference_id,
        link: currentReference.link,
        name: currentReference.name,
      });
    }
  }, [currentReference]);

  useEffect(() => {
    if (!referenceId) return;
    dispatch(mitigatorActions.fetchReference({ reference_id: referenceId }));
  }, [referenceId]);

  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false); // return to view mode
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      link: values.link,
      name: values.name,
      status: "draft",
    };
    const action = isEditMode
      ? mitigatorActions.updateReference({
        id: referenceId,
        status: currentReference?.status,
        ...body,
      })
      : mitigatorActions.createReference(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {referenceId && currentReference && !isEditMode ? (
        <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
          <Row justify="between" align="" className="w-100 pl-16 pr-16">
            <span className="ff-exo2 fw-500 f22 line-28 mb2 c238787">
              Reference
            </span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>

          <Col className="pl-30 pr-26">
            {/* VIEW MODE FORM */}
            <NMForm
              className="w-100"
              ref={viewRef}
              readOnly={true}
              fields={getReferenceFields(true, false)}
              formData={{
                id: currentReference.reference_id,
                link: currentReference.link,
                name: currentReference.name,
              }}
            />
          </Col>
          <Row
            className="pr-36 mt-16 w-100"
            justify="end"
            align="center"
            style={{ gap: "12px" }}
          >
            {currentTab === "draft" && (
              <>
                <Button
                  label="Edit"
                  icon="EDIT"
                  onClick={() => setIsEditMode(true)}
                />
                <Button
                  label="Activate"
                  icon="CHECK_CONFIRM"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateReference({
                        id: referenceId,
                        link: currentReference.link,
                        name: currentReference.name,
                        status: "active",
                      }),
                    );
                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
            {currentTab === "active" && (
              <>
                <Button
                  label="Archive"
                  icon="OPEN_FOLDER"
                  variant="CAUTION"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateReference({
                        id: referenceId,
                        link: currentReference.link,
                        name: currentReference.name,
                        status: "archive",
                      }),
                    );
                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
            {currentTab === "archive" && (
              <>
                <Button
                  label="Unarchive"
                  icon="OPEN_FOLDER"
                  onClick={() => {
                    dispatch(
                      mitigatorActions.updateReference({
                        id: referenceId,
                        link: currentReference.link,
                        name: currentReference.name,
                        status: "draft",
                      }),
                    );
                    publish("MITIGATOR_HIDE_NEW_CONTENT");
                  }}
                />
              </>
            )}
          </Row>
        </Col>
      ) : (
        <>
          <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
            <Row justify="between" align="center" className="w-100 pl-16 pr-16">
              <span className="ff-exo2 fw-500 f22 line-28   mb2 c238787">
                Reference
              </span>
            </Row>
            <NMForm
              className="w-100 pl-30 pr-26"
              ref={_ref}
              // formData={{ id: "Reference ID" }}
              formData={
                isEditMode
                  ? {
                    id: currentReference?.reference_id ?? "",
                    link: currentReference?.link ?? "",
                    name: currentReference?.name ?? "",
                  }
                  : {
                    id: "",
                    link: "",
                    name: "",
                  }
              }
              id="new-reference-form"
              fields={
                isEditMode
                  ? getReferenceFields(false, true)
                  : getReferenceFields(false, false)
              }
            />
            <Row
              className="pr-26 mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              {" "}
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                onClick={handleCancel}
                disabled={submitting}
              />
              <Button
                label="Save"
                icon="SAVE"
                onClick={handleSave}
                loading={submitting}
              />
            </Row>
          </Col>
        </>
      )}
    </div>
  );
};
